import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components
const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(-45deg, #121212, #1a1a2e, #16213e, #0f3460);
  background-size: 400% 400%;
  animation: ${gradient} 15s ease infinite;
  position: relative;
  overflow: hidden;
`;

const SpaceIllustration = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.3;
`;

const Circle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 1;
  max-width: 600px;
  gap: 1.5rem;
`;

const NotFoundTitle = styled(motion.h1)`
  font-size: 8rem;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(90deg, #00dbde, #fc00ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1;
`;

const NotFoundDescription = styled(motion.p)`
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
`;

const NotFoundSubtext = styled(motion.p)`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const HomeButton = styled(motion.button)`
  background: linear-gradient(45deg, #ccff00, #00b4db);
  color: #111;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 2rem;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
  }
`;

const shine = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const createStars = () => {
  const stars = [];
  for (let i = 0; i < 50; i++) {
    const size = Math.random() * 3 + 1;
    stars.push(
      <Circle
        key={i}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animation: `${float} ${Math.random() * 10 + 5}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 5}s`,
        }}
      />
    );
  }
  return stars;
};

const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <NotFoundContainer>
      <SpaceIllustration>{createStars()}</SpaceIllustration>

      <ContentWrapper>
        <NotFoundTitle
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 100 }}
        >
          404
        </NotFoundTitle>

        <NotFoundDescription
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Oops! Page Not Found
        </NotFoundDescription>

        <NotFoundSubtext
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          The page you're looking for doesn't exist or has been moved.
        </NotFoundSubtext>

        <HomeButton
          onClick={() => navigate('/')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          Return Home
        </HomeButton>
      </ContentWrapper>
    </NotFoundContainer>
  );
};

export default NotFoundPage;
